import { useState, useCallback, useRef } from "react";
import type { Toast } from "@/types";

const DEFAULT_DURATION = 5000;

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clear = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, "id">) => {
      const id = crypto.randomUUID();
      const entry: Toast = { ...toast, id };
      setToasts((prev) => [...prev, entry]);

      const duration = toast.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        const timer = setTimeout(() => {
          timers.current.delete(id);
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
        timers.current.set(id, timer);
      }

      return id;
    },
    [],
  );

  const info = useCallback(
    (title: string, message?: string) => push({ type: "info", title, message }),
    [push],
  );

  const success = useCallback(
    (title: string, message?: string) => push({ type: "success", title, message }),
    [push],
  );

  const warning = useCallback(
    (title: string, message?: string) =>
      push({ type: "warning", title, message, duration: 8000 }),
    [push],
  );

  const error = useCallback(
    (title: string, message?: string, action?: Toast["action"]) =>
      push({ type: "error", title, message, duration: 0, action }),
    [push],
  );

  return { toasts, push, dismiss, clear, info, success, warning, error };
}
